// Die Rollen des Abschnitts "Rolle im Haus" aus dem Rollenkatalog (ST-REL-001,
// Rollenkatalog; ADR-0067): welche Rollen sich vergeben lassen und wie eine
// Rolle heisst. Loest die feste Liste ROLE_LABELS am Kontakt ab.
//
// REINES MODELL, kein React, kein DOM.
//
// DER KATALOG WIRD GANZ GELESEN: genau diese Funktion folgt allen Seiten. Der
// Cursor ist undurchsichtig -- er wird nur zurueckgegeben, nie gedeutet, und
// die Seitengroesse bestimmt der Server.

import type { PlainMessage } from './access.ts';
import { appendPage, fetchRoleTypes, type RoleTypeView } from './context-role-types.ts';
import type { SendLike } from './command-client.ts';

/** Der Name einer Rolle, wie ihn der Abschnitt zeigt. */
export type RoleNames = (roleCode: string) => string;

export interface RoleCatalog {
  readonly names: RoleNames;
  /** Die vergebbaren Rollen (freigegeben, nicht beendet), nach Namen geordnet. */
  readonly assignable: readonly string[];
}

/** Kennt der Katalog einen Code nicht, steht dieses Wort da -- nie die Kennung. */
export const UNKNOWN_ROLE_NAME = 'Unbekannte Rolle';

/** Der freigegebene Name; den Namen eines Vorschlags erst, wenn es keinen freigegebenen gibt. */
function shownName(roleType: RoleTypeView): string {
  return roleType.approvedName ?? roleType.name;
}

export function catalogOf(roleTypes: readonly RoleTypeView[]): RoleCatalog {
  const byCode = new Map(roleTypes.map((roleType) => [roleType.roleCode, roleType]));
  const assignable = roleTypes
    .filter((roleType) => roleType.typeStatus === 'active')
    .sort((a, b) => shownName(a).localeCompare(shownName(b), 'de'))
    .map((roleType) => roleType.roleCode);
  return {
    names: (roleCode) => {
      const roleType = byCode.get(roleCode);
      if (roleType === undefined) return UNKNOWN_ROLE_NAME;
      // Eine beendete Rolle bleibt an bestehenden Zuordnungen stehen und sagt es.
      return roleType.typeStatus === 'ended' ? `${shownName(roleType)} (beendet)` : shownName(roleType);
    },
    assignable,
  };
}

export type RoleCatalogResult = { readonly outcome: 'loaded'; readonly catalog: RoleCatalog } | { readonly outcome: 'rejected'; readonly message: PlainMessage };

/**
 * Ohne Katalog fehlen die Namen: der Abschnitt zeigt dann einen ehrlichen
 * Ladefehler statt Kennungen. Derselbe Satz fuer jede Ursache, auch fuer einen
 * Cursor, den der Server zum zweiten Mal nennt (weiterlesen hiesse im Kreis
 * lesen); die Saetze des Rollenkatalogs selbst sprechen von dessen Seite.
 */
export const CATALOG_UNAVAILABLE_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Die Rollen sind gerade nicht erreichbar.',
  explanation: 'Die Rollen ließen sich nicht vollständig laden. Es wurde nichts geändert.',
  nextStep: 'Lade die Rollen erneut. Bleibt es so, wende dich an den Betrieb.',
  retryable: true,
});

export async function readRoleCatalog(options: {
  readonly baseUrl: string;
  readonly accessToken: string;
  readonly send?: SendLike;
}): Promise<RoleCatalogResult> {
  let roleTypes: readonly RoleTypeView[] = [];
  const seen = new Set<string>();
  let cursor: string | undefined;
  do {
    const result = await fetchRoleTypes({
      baseUrl: options.baseUrl,
      accessToken: options.accessToken,
      ...(cursor === undefined ? {} : { cursor }),
      ...(options.send ? { send: options.send } : {}),
    });
    if (result.outcome === 'rejected') return { outcome: 'rejected', message: CATALOG_UNAVAILABLE_MESSAGE };
    roleTypes = appendPage(roleTypes, result.page.roleTypes);
    cursor = result.page.nextCursor;
    if (cursor !== undefined) {
      if (seen.has(cursor)) return { outcome: 'rejected', message: CATALOG_UNAVAILABLE_MESSAGE };
      seen.add(cursor);
    }
  } while (cursor !== undefined);
  return { outcome: 'loaded', catalog: catalogOf(roleTypes) };
}
